import { useEffect, useState } from "react";
import { BookOpen, MapPin, Feather, UserCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const EMPTY = { display_name: "", traveling_from: "", message: "", avatar_url: "" };

export default function GuestBook() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/guestbook")
      .then(r => setEntries(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const display_name = form.display_name.trim();
    const traveling_from = form.traveling_from.trim();
    const message = form.message.trim();
    if (!display_name || !traveling_from || !message) {
      toast.error("Name, where you travel from, and a message are all required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        display_name,
        traveling_from,
        message,
        avatar_url: form.avatar_url.trim() || undefined,
      };
      const r = await api.post("/guestbook", payload);
      setEntries([r.data, ...entries]);
      setForm(EMPTY);
      toast.success("Your mark is left in the book. Safe travels!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign the book. Try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full px-3 py-2 rounded-sm bg-[#1a0a2e] border text-[color:var(--silver)] focus:outline-none focus:ring-1 focus:ring-[color:var(--gold)]";
  const inputStyle = { borderColor: "rgba(212,175,55,0.3)" };

  const fmtDate = (d) => {
    try {
      return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <div className="page-container fade-in" data-testid="guestbook-page">
      <div className="page-eyebrow">leave your mark</div>
      <h1 className="page-title">Traveler's Guest Book</h1>
      <p className="page-sub">
        Every soul who passes through the tavern is welcome to sign the book. Tell us your name, the road that brought you,
        and a word to leave by the fire for those who wander in after you.
      </p>

      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 mt-10">
        {/* SIGN THE BOOK */}
        <div className="parchment-card p-6 md:p-7 h-fit" data-testid="guestbook-form-card">
          <div className="flex items-center gap-2">
            <Feather className="h-5 w-5" style={{ color: "var(--gold)" }} />
            <h2 className="tavern-heading text-2xl gold-text">Sign the Book</h2>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            Dip the quill and leave a few words. The date is recorded for you.
          </p>

          <form className="mt-5 space-y-3" onSubmit={submit}>
            <div>
              <label className="page-eyebrow" style={{ fontSize: "0.7rem" }}>Display Name *</label>
              <input className={`${input} mt-1`} placeholder="e.g. Wandering Elowen"
                value={form.display_name} maxLength={60}
                onChange={e => setForm({ ...form, display_name: e.target.value })}
                required data-testid="gb-name" style={inputStyle} />
            </div>

            <div>
              <label className="page-eyebrow" style={{ fontSize: "0.7rem" }}>Traveling From *</label>
              <input className={`${input} mt-1`} placeholder="e.g. The Misty Vale, Portland, the far side of the moon…"
                value={form.traveling_from} maxLength={80}
                onChange={e => setForm({ ...form, traveling_from: e.target.value })}
                required data-testid="gb-from" style={inputStyle} />
            </div>

            <div>
              <label className="page-eyebrow" style={{ fontSize: "0.7rem" }}>Message *</label>
              <textarea className={`${input} mt-1`} rows={5} placeholder="A word for the tavern and its travelers…"
                value={form.message} maxLength={1000}
                onChange={e => setForm({ ...form, message: e.target.value })}
                required data-testid="gb-message" style={inputStyle} />
              <div className="text-[0.7rem] mt-1 text-right" style={{ color: "var(--muted)" }}>
                {form.message.length}/1000
              </div>
            </div>

            <div>
              <label className="page-eyebrow" style={{ fontSize: "0.7rem" }}>Avatar / Sigil Image URL (optional)</label>
              <input className={`${input} mt-1 text-xs`} placeholder="Paste an image URL for your sigil (optional)"
                value={form.avatar_url}
                onChange={e => setForm({ ...form, avatar_url: e.target.value })}
                data-testid="gb-avatar" style={inputStyle} />
              {form.avatar_url.trim() && (
                <img src={form.avatar_url} alt="sigil preview"
                  className="mt-2 h-14 w-14 rounded-full object-cover"
                  style={{ border: "1px solid rgba(212,175,55,0.35)" }}
                  onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
              )}
            </div>

            <button type="submit" className="gold-btn glow-pulse" disabled={submitting} data-testid="gb-submit">
              <Send className="h-4 w-4" /> {submitting ? "Signing…" : "Leave Your Mark"}
            </button>
          </form>
        </div>

        {/* THE BOOK */}
        <div data-testid="guestbook-entries">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" style={{ color: "var(--gold)" }} />
            <h2 className="tavern-heading text-2xl gold-text">Signatures by the Fire</h2>
          </div>

          {loading ? (
            <p className="text-sm mt-5" style={{ color: "var(--muted)" }}>Turning the pages…</p>
          ) : entries.length === 0 ? (
            <div className="parchment-card p-6 mt-5" data-testid="guestbook-empty">
              <p className="tavern-script text-xl" style={{ color: "var(--orchid-light)" }}>The book is blank.</p>
              <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Be the first traveler to sign it.</p>
            </div>
          ) : (
            <ul className="mt-5 space-y-4">
              {entries.map((en) => (
                <li key={en.id} className="parchment-card p-5" data-testid={`gb-entry-${en.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {en.avatar_url ? (
                        <img src={en.avatar_url} alt={en.display_name}
                          className="h-12 w-12 rounded-full object-cover"
                          style={{ border: "1px solid rgba(212,175,55,0.35)" }}
                          onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <div className="h-12 w-12 grid place-items-center rounded-full"
                          style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(212,175,55,0.3)" }}>
                          <UserCircle2 className="h-6 w-6" style={{ color: "rgba(212,175,55,0.6)" }} />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="tavern-heading text-lg gold-text">{en.display_name}</span>
                        <span className="text-xs flex items-center gap-1" style={{ color: "var(--orchid-light)" }}>
                          <MapPin className="h-3 w-3" /> {en.traveling_from}
                        </span>
                      </div>
                      <div className="text-[0.7rem] uppercase tracking-widest mt-0.5" style={{ color: "var(--muted)" }}>
                        {fmtDate(en.created_at)}
                      </div>
                      <p className="mt-2 leading-relaxed whitespace-pre-wrap" style={{ color: "var(--silver)" }}>
                        {en.message}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
